import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, map } from 'rxjs';
import { LanguageService } from '../../../core/services/language.service';
import { TableColumn } from '../../../osp-ui/components/data/table/contract/table-column.contract';
import {
  COLUMN_DEFAULT_MIN_WIDTH_PX,
  COLUMN_DEFAULT_WIDTH_PX,
} from '../../../osp-ui/components/data/table/table.constant';
import { TableCellNotificationActionsComponent } from '../../../shared/components/table-cell/table-cell-notification-actions/table-cell-notification-actions.component';
import { PAGEABLE_DATASOURCE_DEFAULT_PAGE_SIZE } from '../../../shared/constants/pageable-datasource.const';
import { EquipmentTypeFilterView } from '../../../shared/filters/contract/filter-view/equipment-type-filter-view.contract';
import { TranslateI18nObjectPipe } from '../../../shared/pipes/translate-i18n-object.pipe';
import { ViewAlarmRule } from '../contract/view-alarm-rule.contract';
import { AlarmColumnKeys } from '../enums/alarm-column-keys.enum';
import { EquipmentAlarmRuleSelectionType } from '../enums/equipment-alarm-rule-selection-type.enum';
import { AlarmLevelResolverPipe } from '../pipes/alarm-level-resolver.pipe';
import { AlarmSelectionTypeResolverPipe } from '../pipes/alarm-selection-type-resolver.pipe';
import { AlarmTypeResolverPipe } from '../pipes/alarm-type-resolver.pipe';
import { NotificationApiService } from './notification-api.service';

@Injectable({ providedIn: 'root' })
export class NotificationsListConfigService {
  public readonly availableColumns: TableColumn<ViewAlarmRule>[] = [
    {
      key: AlarmColumnKeys.ALARM_TYPE,
      header: this.translate('modules.notification.base.alarmType'),
      readableName: this.translate('modules.notification.base.alarmType'),
      resizable: true,
      widthPx: COLUMN_DEFAULT_WIDTH_PX,
      cbValue: (row) => this.alarmTypeResolver.transform(row.alarmType),
    },
    {
      key: AlarmColumnKeys.SELECTION_TYPE,
      header: this.translate('modules.notification.notificationList.selectBy'),
      readableName: this.translate('modules.notification.notificationList.selectBy'),
      resizable: true,
      widthPx: COLUMN_DEFAULT_WIDTH_PX,
      cbValue: (row) => this.alarmSelectionTypeResolver.transform(row.selectionType),
    },
    {
      key: AlarmColumnKeys.LABELS_OR_EQUIPMENT_TYPES,
      header: this.translate('modules.notification.notificationList.associatedSelection'),
      readableName: this.translate('modules.notification.notificationList.associatedSelection'),
      resizable: true,
      contentTooltipOnOverflow: true,
      widthPx: COLUMN_DEFAULT_WIDTH_PX,
      cbValue: (row) => this.resolveEquipmentOrLabel(row),
    },
    {
      key: AlarmColumnKeys.ALARM_LEVEL,
      header: this.translate('modules.notification.notificationList.alarmLevel'),
      readableName: this.translate('modules.notification.notificationList.alarmLevel'),
      resizable: true,
      widthPx: COLUMN_DEFAULT_WIDTH_PX,
      cbValue: (row) => this.alarmLevelResolver.transform(row.alarmLevel),
    },
    {
      key: AlarmColumnKeys.ALARM_MESSAGE,
      field: 'alarmMessage',
      header: this.translate('modules.notification.notificationList.alarmMessage'),
      readableName: this.translate('modules.notification.notificationList.alarmMessage'),
      resizable: true,
      widthPx: COLUMN_DEFAULT_WIDTH_PX,
    },
    {
      key: AlarmColumnKeys.ACTIONS,
      header: '',
      readableName: '',
      resizable: true,
      widthPx: COLUMN_DEFAULT_MIN_WIDTH_PX,
      component: TableCellNotificationActionsComponent,
    },
  ];
  protected _selectedColumnKeys$ = new BehaviorSubject<string[]>([]);
  private _pageSize$ = new BehaviorSubject<number>(PAGEABLE_DATASOURCE_DEFAULT_PAGE_SIZE);
  private _onClickDetails$ = new Subject<ViewAlarmRule>();
  private _equipmentTypes$ = new BehaviorSubject<EquipmentTypeFilterView[]>([]);
  private readonly DEFAULT_PAGE_SIZE = 25;
  private readonly EMPTY_PLACEHOLDER = '';

  public readonly contentEmitters = {
    onClickDetails: this._onClickDetails$.asObservable(),
  };

  public readonly selectedColumns$ = this._selectedColumnKeys$.pipe(
    map((keys) => keys?.map((key) => this.getColumn(key))),
    map((tableColumns) => <TableColumn<ViewAlarmRule>[]>tableColumns?.filter(Boolean)),
  );

  get pageSize(): number {
    return this._pageSize$.getValue();
  }

  constructor(
    protected languageService: LanguageService,
    protected alarmLevelResolver: AlarmLevelResolverPipe,
    protected alarmTypeResolver: AlarmTypeResolverPipe,
    protected alarmSelectionTypeResolver: AlarmSelectionTypeResolverPipe,
    protected translateI18nObjectPipe: TranslateI18nObjectPipe,
    protected notificationsApiService: NotificationApiService,
  ) {
    this.getEquipmentTypes();
    this.initConfigs();
  }

  getColumn(columnKey: string): TableColumn<ViewAlarmRule> | null {
    return this.availableColumns.find(({ key }) => columnKey === key) || null;
  }

  savePageSize(size: number): void {
    if (size && size !== this.pageSize) {
      this._pageSize$.next(size);
    }
  }

  protected initConfigs(): void {
    this._pageSize$.next(this.DEFAULT_PAGE_SIZE);
    const columnKeys = this.availableColumns.map((col) => col.key);
    this._selectedColumnKeys$.next(columnKeys);
  }

  protected getEquipmentTypes(): void {
    this.notificationsApiService.getDistinctEquipmentTypes().subscribe((equipmentTypes) => {
      this._equipmentTypes$.next(equipmentTypes);
    });
  }

  resolveEquipmentOrLabel(alarmRule: ViewAlarmRule): string {
    if (!alarmRule?.selectionType) {
      return '';
    }
    switch (alarmRule.selectionType) {
      case EquipmentAlarmRuleSelectionType.LABEL:
        return alarmRule?.labels?.join(', ') || this.EMPTY_PLACEHOLDER;
      case EquipmentAlarmRuleSelectionType.EQUIPMENT_TYPE:
        return this.mapToCategory2Names(alarmRule.equipmentTypeIds).join(', ') || this.EMPTY_PLACEHOLDER;
      case EquipmentAlarmRuleSelectionType.ALL_EQUIPMENT:
        return this.EMPTY_PLACEHOLDER;
      case EquipmentAlarmRuleSelectionType.CUSTOMER:
        return alarmRule?.sharingCustomers?.map((customer) => customer.name).join(', ') || this.EMPTY_PLACEHOLDER;
      case EquipmentAlarmRuleSelectionType.EQUIPMENT:
        return alarmRule?.equipments?.map((equipment) => equipment.name).join(', ') || this.EMPTY_PLACEHOLDER;
      default:
        return this.translate('general.unknown').toLowerCase();
    }
  }

  protected mapToCategory2Names(equipmentTypeIds: string[] | undefined): string[] {
    const typeNames: string[] = [];

    if (equipmentTypeIds) {
      this._equipmentTypes$.getValue().forEach((typeGroup) => {
        typeGroup.subTypes.forEach((subType) => {
          if (equipmentTypeIds.includes(subType.equipmentTypeId)) {
            typeNames.push(this.translateI18nObjectPipe.transform(subType.category2Name));
          }
        });
      });
    }

    return typeNames.filter(Boolean);
  }

  protected translate(key: string): string {
    return this.languageService.getInstant(key);
  }
}
